import { useEffect, useState } from "react";
import { Modal } from 'react-bootstrap-v5';
import { IApiKeyCriada } from "../../interfaces/IApiKey";
import ApiKeyService from "../../services/ApiKeyService";
import {
    MAX_DOMINIOS,
    TAMANHO_MAXIMO_NOME,
    expiraEmIso,
    normalizarDominio,
    validadeMaxima,
    validadeMinima,
    validadePadrao,
    validarDominio,
} from "./apiKeyUtils";

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface Props {
    show: boolean;
    // Só para Master: cliente escolhido na tela. Administrador não envia (o backend usa o dele).
    clienteId?: number;
    onFechar: () => void;
    onCriada: (chave: IApiKeyCriada) => void;
}

// DEVN3-627: criação de API Key — nome, domínios de origem e validade.
export default function ApiKeyModalCriar({ show, clienteId, onFechar, onCriada }: Props) {
    const { t } = useTranslation();
    const [nome, setNome] = useState<string>('');
    const [dominios, setDominios] = useState<string[]>([]);
    const [novoDominio, setNovoDominio] = useState<string>('');
    const [erroDominio, setErroDominio] = useState<string | null>(null);
    const [validade, setValidade] = useState<string>(validadePadrao());
    const [salvando, setSalvando] = useState<boolean>(false);

    useEffect(() => {
        if (show) {
            setNome('');
            setDominios([]);
            setNovoDominio('');
            setErroDominio(null);
            setValidade(validadePadrao());
        }
    }, [show]);

    function adicionarDominio() {
        const erro = validarDominio(novoDominio);
        if (erro) {
            setErroDominio(erro);
            return;
        }

        const origem = normalizarDominio(novoDominio);
        if (dominios.includes(origem)) {
            setErroDominio(t('Este domínio já foi adicionado.'));
            return;
        }
        if (dominios.length >= MAX_DOMINIOS) {
            setErroDominio(t(`Máximo de ${MAX_DOMINIOS} domínios por chave.`));
            return;
        }

        setDominios([...dominios, origem]);
        setNovoDominio('');
        setErroDominio(null);
    }

    const nomeValido = nome.trim().length > 0 && nome.trim().length <= TAMANHO_MAXIMO_NOME;
    const validadeValida = !!validade && validade >= validadeMinima() && validade <= validadeMaxima();
    const podeSalvar = nomeValido && validadeValida && dominios.length > 0 && !salvando;

    async function salvar() {
        setSalvando(true);
        const criada = await ApiKeyService.criar({
            clienteId,
            nome: nome.trim(),
            dominios,
            expiraEm: expiraEmIso(validade),
        });
        setSalvando(false);

        if (criada) {
            onCriada(criada);
        }
    }

    return <Modal size='lg' centered={true} show={show} onHide={() => !salvando && onFechar()}>
        <div className="modal-content">
            <div className="modal-header">
                <h5 className="modal-title titulo-modal">{t('Nova API Key')}</h5>
                <div className="btn btn-icon btn-sm btn-active-light-primary ms-2">
                    <i onClick={() => !salvando && onFechar()} className="fas fa-times" style={{ color: '#5E6397' }}></i>
                </div>
            </div>
            <div className="modal-body">
                <form className="row g-3" onSubmit={(e) => e.preventDefault()}>
                    <div className="col-md-8">
                        <label htmlFor="api-key-nome" className="form-label required">{t('Nome')}</label>
                        <input id="api-key-nome" type="text" className="form-control" maxLength={TAMANHO_MAXIMO_NOME}
                            placeholder={t('Ex.: Integração ERP')} value={nome} onChange={(e) => setNome(e.target.value)} />
                    </div>
                    <div className="col-md-4">
                        <label htmlFor="api-key-validade" className="form-label required">{t('Válida até')}</label>
                        <input id="api-key-validade" type="date" className="form-control"
                            min={validadeMinima()} max={validadeMaxima()} value={validade} onChange={(e) => setValidade(e.target.value)} />
                        <div className="form-text">{t('Máximo de 1 ano.')}</div>
                    </div>

                    <div className="col-12">
                        <label htmlFor="api-key-dominio" className="form-label required">{t('Domínios de origem')}</label>
                        <div className="input-group">
                            <input id="api-key-dominio" type="text" className={`form-control ${erroDominio ? 'is-invalid' : ''}`}
                                placeholder="https://erp.suaempresa.com.br" value={novoDominio}
                                onChange={(e) => { setNovoDominio(e.target.value); setErroDominio(null); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionarDominio(); } }} />
                            <button type="button" className="btn btn-sm btn-primario" onClick={adicionarDominio}>{t('Adicionar')}</button>
                        </div>
                        {erroDominio && <div className="text-danger mt-1">{t(erroDominio)}</div>}
                        <div className="form-text">
                            {t('Chamadas feitas de um navegador só são aceitas a partir destes domínios. Somente https.')}
                        </div>

                        <div className="mt-3">
                            {dominios.length === 0 && <span className="text-muted">{t('Nenhum domínio adicionado.')}</span>}
                            {dominios.map((dominio) => (
                                <span key={dominio} className="badge badge-light-primary fs-7 me-2 mb-2">
                                    {dominio}
                                    <i className="fas fa-times ms-2" style={{ cursor: 'pointer' }} title={t('Remover')}
                                        onClick={() => setDominios(dominios.filter((d) => d !== dominio))}></i>
                                </span>
                            ))}
                        </div>
                    </div>
                </form>
            </div>
            <div className="modal-footer" style={{ margin: '0 auto' }}>
                <button disabled={salvando} onClick={onFechar} type="button" className="btn btn-modal-cancelar me-5"> {t('CANCELAR')} </button>
                <button disabled={!podeSalvar} onClick={salvar} type="button" className="btn btn-primario ms-5">
                    {!salvando && <> {t('GERAR CHAVE')} </>}
                    {salvando && (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                            {t('Gerando...')}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                    )}
                </button>
            </div>
        </div>
    </Modal>;
}
